import { AnalyticsTranslationKeys } from 'intl/keys';
import { DateTime } from 'luxon';
import {
  AnyFilters,
  DateRangeObject,
  FilterValue,
  FilterValues,
  isDateRangeObject,
} from 'shared/types/analytics';
import { deepEqual } from 'shared/utils/deepEqual';
import { fromDate14DaysEarlier, toDateToday } from './index';

/** Filters of any chart: only their values and options are read here. */
type ChartFilters = AnyFilters<unknown, unknown, unknown>;

interface FilterValueGroup {
  labelId: string;
  filters: FilterValue[];
}

const DEFAULT_DATE_RANGE_DAYS = 14;

/**
 * Multiselect values (string arrays) are sorted, so ['A','B'] and ['B','A']
 * compare and serialize alike: they are set-like selections.
 */
export function normalizeFilterValue(value: unknown): unknown {
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return [...value].sort();
  }
  return value;
}

function toFilterValueGroup(group: FilterValueGroup): FilterValueGroup {
  return {
    labelId: group.labelId,
    filters: group.filters.map((filter) => ({
      labelId: filter.labelId,
      value: normalizeFilterValue(filter.value),
    })),
  };
}

/** The values of the filters, as they are saved and sent to the chart calculation. */
export function extractFiltersValues(filters: FilterValues): FilterValues;
export function extractFiltersValues(filters: FilterValues | undefined): FilterValues | undefined;
export function extractFiltersValues(filters: FilterValues | undefined): FilterValues | undefined {
  if (!filters) {
    return undefined;
  }

  return {
    dataScope: toFilterValueGroup(filters.dataScope),
    ...(filters.chartSettings && { chartSettings: toFilterValueGroup(filters.chartSettings) }),
    ...(filters.presented && { presented: toFilterValueGroup(filters.presented) }),
  };
}

/** Signature of the filter values, to skip saving or requesting what did not change. */
export function getChartFiltersSignature(filters: FilterValues | undefined): string {
  return JSON.stringify(extractFiltersValues(filters) ?? null);
}

function toFilterOptions(group: ChartFilters['dataScope'] | undefined) {
  return group?.filters.map((filter) => ({
    labelId: filter.labelId,
    availableCount: filter.available?.length ?? 0,
  }));
}

/** Whether two filter sets have the same values and the same options (labels, number of choices). */
export function hasSameChartFilterValues(
  previous: ChartFilters | undefined,
  next: ChartFilters | undefined
): boolean {
  return (
    deepEqual(extractFiltersValues(previous), extractFiltersValues(next)) &&
    deepEqual(toFilterOptions(previous?.dataScope), toFilterOptions(next?.dataScope)) &&
    deepEqual(toFilterOptions(previous?.chartSettings), toFilterOptions(next?.chartSettings)) &&
    deepEqual(toFilterOptions(previous?.presented), toFilterOptions(next?.presented))
  );
}

/** The range every chart starts with: the last 14 days, ending today. */
export function createDefaultDateRange(timezone: string): DateRangeObject {
  return { from: fromDate14DaysEarlier(timezone), to: toDateToday(timezone) };
}

/**
 * A range as long as the default one (the last 14 days) moves to end today,
 * like the default; any other range stays as chosen.
 */
export function moveDefaultLengthRangeToToday(range: DateRangeObject, timezone: string): DateRangeObject {
  // Rounded: a range across a DST change is not a whole number of days.
  const rangeDays = Math.round(DateTime.fromISO(range.to).diff(DateTime.fromISO(range.from), 'days').days);
  if (rangeDays !== DEFAULT_DATE_RANGE_DAYS) {
    return range;
  }
  return createDefaultDateRange(timezone);
}

/**
 * Saved filters with the default date range (the last 14 days) moved to end
 * today: it means "recent", not the days it was saved on.
 */
export function moveDefaultDateRangeToToday(filters: FilterValues, timezone: string): FilterValues {
  const [firstFilter, ...otherFilters] = filters.dataScope.filters;
  if (firstFilter?.labelId !== AnalyticsTranslationKeys.dateRange || !isDateRangeObject(firstFilter.value)) {
    return filters;
  }

  const movedRange = moveDefaultLengthRangeToToday(firstFilter.value, timezone);
  if (movedRange === firstFilter.value) {
    return filters;
  }
  return {
    ...filters,
    dataScope: { ...filters.dataScope, filters: [{ ...firstFilter, value: movedRange }, ...otherFilters] },
  };
}
